import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { Delete, FilterAltOff, CheckCircle, ErrorOutline } from '@mui/icons-material';
import { customerSupportAPI, citiesAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

const getSupportInquirySource = (support) =>
  String(support?.inquirySource ?? support?.InquirySource ?? support?.inquiry_source ?? '').trim();

const INQUIRY_SOURCE_OPTIONS = ['App', 'Customer'];

const getSupportRowId = (support) =>
  support?.id ?? support?.supportId ?? support?.SupportId ?? null;

function isSuperAdminUser() {
  return /super\s*admin/i.test(getAdminUser().role || '');
}

const CustomerSupport = () => {
  const { t } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerSupports, setCustomerSupports] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedSupport, setSelectedSupport] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState('');
  const [resolveNotes, setResolveNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    cityId: '',
    inquirySource: '',
  });

  const [debouncedNameSearch, setDebouncedNameSearch] = useState('');
  const debouncedNameSearchRef = useRef(debouncedNameSearch);
  useEffect(() => {
    debouncedNameSearchRef.current = debouncedNameSearch;
  }, [debouncedNameSearch]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.search || '').trim();
      const prev = debouncedNameSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedNameSearch(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.search]);

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const citiesList = response.data.payload;
        setCities(citiesList);

        if (citiesList.length > 0) {
          const firstCity = citiesList[0];
          setFilters(prev => ({
            ...prev,
            cityId: firstCity.id
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCustomerSupports = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await customerSupportAPI.getCustomerSupports(
        filters.cityId,
        page,
        rowsPerPage,
        { name: debouncedNameSearch },
      );
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        setCustomerSupports(response.data.payload);
        const apiTotal = response.data.totalRecord;
        setTotalCount(
          apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : response.data.payload.length,
        );
      } else {
        setCustomerSupports([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching customer supports:', error);
      setCustomerSupports([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.cityId, page, rowsPerPage, debouncedNameSearch]);

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: '',
      inquirySource: ''
    }));
    setDebouncedNameSearch('');
    setPage(0);
  };

  const matchesInquirySourceFilter = (support) => {
    if (!filters.inquirySource) return true;
    return (
      getSupportInquirySource(support).toLowerCase() === String(filters.inquirySource).toLowerCase()
    );
  };

  const inquirySourceLabel = (value) => {
    const v = String(value || '').trim();
    if (!v) return '—';
    if (v.toLowerCase() === 'app') return t('support.inquirySourceApp');
    if (v.toLowerCase() === 'customer') return t('support.inquirySourceCustomer');
    return v;
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (filters.cityId) {
      fetchCustomerSupports();
    }
  }, [filters.cityId, fetchCustomerSupports]);

  const visibleSupports = useMemo(() => {
    const q = debouncedNameSearch.trim().toLowerCase();
    const hay = (v) => String(v ?? '').toLowerCase();
    return customerSupports.filter((support) => {
      if (!matchesInquirySourceFilter(support)) return false;
      if (!q) return true;
      return hay(support.name).includes(q) || hay(support.Name).includes(q);
    });
  }, [customerSupports, debouncedNameSearch, filters.inquirySource]);

  const openResolveDialog = (support) => {
    setSelectedSupport(support);
    setResolveStatus(support.isIssueResolved ? 'yes' : 'no');
    setResolveNotes(support.notes || support.Notes || '');
    setResolveDialogOpen(true);
  };

  const closeResolveDialog = () => {
    setResolveDialogOpen(false);
    setSelectedSupport(null);
    setResolveStatus('');
    setResolveNotes('');
  };

  const handleResolveSubmit = async () => {
    try {
      setIsSubmitting(true);
      await customerSupportAPI.updateCustomerSupport({
        ...selectedSupport,
        isIssueResolved: resolveStatus === 'yes',
        notes: resolveNotes
      });
      fetchCustomerSupports();
      closeResolveDialog();
      setSnackbar({ open: true, message: t('support.toastStatusOk'), severity: 'success' });
    } catch (error) {
      console.error('Error updating issue status:', error);
      setSnackbar({ open: true, message: t('support.statusError'), severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (support) => {
    const supportId = getSupportRowId(support);
    if (supportId == null || supportId === '') return;
    if (!browserGlobal.confirm(t('support.confirmDelete'))) return;
    try {
      setIsSubmitting(true);
      await customerSupportAPI.deleteCustomerSupport(supportId);
      fetchCustomerSupports();
    } catch (error) {
      console.error('Error deleting customer support:', error);
      alert(t('support.deleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={1}>
      <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
        {t('support.title')}
      </Typography>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 1,
            width: '100%',
            '& .support-filter-field': {
              flex: { xs: '1 1 100%', md: '1 1 0' },
              minWidth: { md: 0 },
              maxWidth: { xs: '100%', md: 200 },
            },
            '& .support-filter-search': {
              flex: { xs: '1 1 100%', md: '1.25 1 0' },
              minWidth: { md: 140 },
              maxWidth: { xs: '100%', md: 260 },
            },
            '& .support-filter-clear': {
              flex: '0 0 auto',
              alignSelf: { xs: 'flex-start', md: 'center' },
              ml: { md: 'auto' },
            },
          }}
        >
          <FormControl className="support-filter-field" size="small" fullWidth>
            <InputLabel>{t('districts.filterCity')}</InputLabel>
            <Select
              value={filters.cityId}
              label={t('districts.filterCity')}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  cityId: e.target.value
                }));
                setPage(0);
              }}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            className="support-filter-search"
            size="small"
            fullWidth
            label={t('common.name')}
            placeholder={t('support.searchPh')}
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                search: e.target.value
              }));
            }}
          />
          <FormControl className="support-filter-field" size="small" fullWidth>
            <InputLabel id="support-inquiry-filter-label">{t('support.filterInquirySource')}</InputLabel>
            <Select
              labelId="support-inquiry-filter-label"
              value={filters.inquirySource}
              label={t('support.filterInquirySource')}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  inquirySource: e.target.value
                }));
                setPage(0);
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(124, 58, 237, 0.35)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0F3D3E'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0F3D3E'
                }
              }}
            >
              <MenuItem value="">{t('support.filterInquirySourceAll')}</MenuItem>
              {INQUIRY_SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {inquirySourceLabel(opt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className="support-filter-clear"
            variant="outlined"
            size="small"
            startIcon={<FilterAltOff />}
            onClick={handleClearFilters}
            sx={clearFilterButtonSx}
          >
            {t('common.clear')}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.email')}</TableCell>
                <TableCell>{t('support.colMessage')}</TableCell>
                <TableCell>{t('common.city')}</TableCell>
                <TableCell>{t('support.colInquirySource')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('support.colNotes')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleSupports.map((support) => (
                  <TableRow 
                    key={getSupportRowId(support) ?? support.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#ebf4f4',
                      }
                    }}
                  >
                    <TableCell>{support.name}</TableCell>
                    <TableCell>{support.emailId}</TableCell>
                    <TableCell>
                      <Tooltip title={support.message} arrow placement="top">
                        <Typography 
                          sx={{ 
                            maxWidth: 200, 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            cursor: 'pointer'
                          }}
                        >
                          {support.message}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{support.cityName}</TableCell>
                    <TableCell>
                      {getSupportInquirySource(support) ? (
                        <Chip
                          size="small"
                          label={inquirySourceLabel(getSupportInquirySource(support))}
                          variant="outlined"
                          sx={{ borderColor: 'rgba(124, 58, 237, 0.35)', fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={support.isIssueResolved ? t('support.colResolved') : t('support.colPending')}
                        color={support.isIssueResolved ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {support.notes || support.Notes ? (
                        <Tooltip title={support.notes || support.Notes} arrow placement="top">
                          <Typography 
                            sx={{ 
                              maxWidth: 180, 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                              cursor: 'pointer'
                            }}
                          >
                            {support.notes || support.Notes}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          onClick={() => openResolveDialog(support)}
                          color={support.isIssueResolved ? 'success' : 'warning'}
                          size="small"
                          title={t('support.tooltipStatus')}
                          disabled={isSubmitting}
                        >
                          {support.isIssueResolved ? <CheckCircle /> : <ErrorOutline />}
                        </IconButton>
                        {isSuperAdminUser() && (
                          <IconButton
                            onClick={() => handleDelete(support)}
                            color="error"
                            size="small"
                            title={t('common.delete')}
                            disabled={isSubmitting}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={resolveDialogOpen} onClose={closeResolveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('support.updateStatusTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('support.resolveQuestion')}</InputLabel>
              <Select
                value={resolveStatus}
                label={t('support.resolveQuestion')}
                onChange={(e) => setResolveStatus(e.target.value)}
              >
                <MenuItem value="yes">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <span>{t('common.yes')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="no">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline color="warning" />
                    <span>{t('common.no')}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('support.fieldNotes')}
              multiline
              rows={3}
              fullWidth
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              placeholder={t('support.fieldNotes')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolveDialog} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleResolveSubmit} 
            variant="contained" 
            color={resolveStatus === 'yes' ? 'success' : 'warning'}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? t('common.updating') : t('promotions.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerSupport;
